// Localhost-only debug surface for Playwright specs.
// Exposes a few read-only accessors on window so e2e tests can inspect
// internal state (peer IDs, gameState) without driving the full UI.
//
// Gated to localhost so the same code can ship to production safely —
// in prod the global is never written, so it can't be exploited.

import { getMyPeerId, getActiveConnections, isPeerInitialized } from '../services/MultiplayerService.js';
import { getGameState, queueEvent, EVENT_TYPES } from '../gameplay/battle-core.js';

// Lazy-loaded battle modules. Pulling renderBattleFrame + _testForceAimLocked
// statically dragged the entire battle subsystem (renderer, AttackSystem,
// physics, themes, etc.) into the entry bundle, doubling the mobile-network
// initial load even though the e2e hooks below are never called in prod.
// Cached after the first call so repeated test invocations don't re-fetch.
let _renderBattleFrame = null;
let _testForceAimLocked = null;
let _battleManagerModule = null;
async function loadRenderBattleFrame() {
    if (_renderBattleFrame) return _renderBattleFrame;
    const m = await import('../gameplay/battle-renderer.js');
    _renderBattleFrame = m.renderBattleFrame;
    return _renderBattleFrame;
}
async function loadTestForceAimLocked() {
    if (_testForceAimLocked) return _testForceAimLocked;
    const m = await loadBattleManagerModule();
    _testForceAimLocked = m._testForceAimLocked;
    return _testForceAimLocked;
}
async function loadBattleManagerModule() {
    if (_battleManagerModule) return _battleManagerModule;
    _battleManagerModule = await import('../gameplay/battle-manager.js');
    return _battleManagerModule;
}

function isLocalhost() {
    try {
        if (typeof window === 'undefined') return false;
        const h = window.location && window.location.hostname;
        return h === 'localhost' || h === '127.0.0.1';
    } catch (_) {
        return false;
    }
}

export function installTestHooks() {
    if (!isLocalhost()) return;
    window.__SNORDELUXE_DEBUG__ = {
        getMyPeerId,
        getActiveConnections,
        isPeerInitialized,
        getGameState,
        // Forces a battle-end. Used by the e2e suite to verify the
        // results screen labels win/loss correctly without having to
        // actually play out a kill — handlers subscribed to BATTLE_VICTORY
        // / BATTLE_DEFEAT (handleBattleEnd_Manager) run on this client.
        forceBattleEnd: (result) => {
            const gs = getGameState();
            if (!gs || !gs.battleStatus) return;
            gs.battleStatus.battleResult = result === 'win' ? 'win' : 'loss';
            const type = result === 'win' ? EVENT_TYPES.BATTLE_VICTORY : EVENT_TYPES.BATTLE_DEFEAT;
            queueEvent({ type, payload: { winnerId: 'debug', finalWave: gs.battleStatus.waveNumber || 1 } });
        },
        // Drive a PLAYER_INPUT_RECEIVED with an explicit aimAngle to
        // exercise the directional-attack pipeline end-to-end without
        // depending on real mouse input + pointer-lock.
        fireAttackWithAim: (playerId, aimAngle, attackIndex = 0) => {
            queueEvent({
                type: EVENT_TYPES.PLAYER_INPUT_RECEIVED,
                payload: { playerId, inputType: 'ATTACK_INTENT', attackIndex, aimAngle },
            });
        },
        // Same as fireAttackWithAim but with chargeMs so e2e can verify
        // the hold-to-charge pipeline (damage scaling + cooldown penalty)
        // without depending on real key-hold timing.
        fireChargedAttack: (playerId, chargeMs, aimAngle = 0, attackIndex = 0) => {
            queueEvent({
                type: EVENT_TYPES.PLAYER_INPUT_RECEIVED,
                payload: { playerId, inputType: 'ATTACK_INTENT', attackIndex, aimAngle, chargeMs },
            });
        },
        // Spawn an attack directly into gameState.activeAttacks, bypassing
        // the cooldown gate. Used by AI-block-reaction tests to guarantee
        // there's a long-lived projectile in flight for the AI to react
        // to (the normal fireAttackWithAim path is gated by attack cooldown
        // and the projectile may collide before the AI's reactionLatency).
        // Render one frame to a stub 2D context that counts the
        // calls to known signature methods (strokeText with the "Aim
        // locked" hint = wheel was drawn). Returns the call counts so
        // e2e can detect "did the wheel render this frame?" without
        // depending on canvas pixel reads (which differ across headless
        // engines).
        renderFrameToStub: async () => {
            const counts = { aimHint: 0, beginPath: 0, arc: 0, stroke: 0, fill: 0 };
            // Stub ctx — covers every Canvas 2D method the renderer
            // touches. Missing one throws inside renderBattleFrame and
            // skips drawAimWheel entirely, so be liberal.
            const ctx = {
                canvas: { width: 800, height: 600 },
                save() {}, restore() {},
                translate() {}, scale() {}, rotate() {}, transform() {}, setTransform() {}, resetTransform() {},
                beginPath() { counts.beginPath++; },
                closePath() {},
                arc() { counts.arc++; },
                arcTo() {},
                ellipse() {},
                rect() {},
                roundRect() {},
                moveTo() {}, lineTo() {},
                quadraticCurveTo() {}, bezierCurveTo() {},
                clip() {},
                stroke() { counts.stroke++; },
                fill() { counts.fill++; },
                fillRect() {}, strokeRect() {}, clearRect() {},
                setLineDash() {}, getLineDash() { return []; },
                fillText() {},
                strokeText(text) { if (String(text).includes('Aim locked')) counts.aimHint++; },
                measureText() { return { width: 0 }; },
                createLinearGradient() { return { addColorStop() {} }; },
                createRadialGradient() { return { addColorStop() {} }; },
                createPattern() { return null; },
                drawImage() {},
                getImageData() { return { data: new Uint8ClampedArray(4), width: 1, height: 1 }; },
                putImageData() {},
                createImageData() { return { data: new Uint8ClampedArray(4), width: 1, height: 1 }; },
                set fillStyle(v) {}, get fillStyle() { return '#000'; },
                set strokeStyle(v) {}, get strokeStyle() { return '#000'; },
                set lineWidth(v) {}, get lineWidth() { return 1; },
                set lineJoin(v) {}, get lineJoin() { return ''; },
                set miterLimit(v) {}, get miterLimit() { return 0; },
                set lineDashOffset(v) {}, get lineDashOffset() { return 0; },
                set shadowColor(v) {}, get shadowColor() { return ''; },
                set shadowBlur(v) {}, get shadowBlur() { return 0; },
                set shadowOffsetX(v) {}, get shadowOffsetX() { return 0; },
                set shadowOffsetY(v) {}, get shadowOffsetY() { return 0; },
                set globalAlpha(v) {}, get globalAlpha() { return 1; },
                set globalCompositeOperation(v) {}, get globalCompositeOperation() { return ''; },
                set lineCap(v) {}, get lineCap() { return ''; },
                set font(v) {}, get font() { return ''; },
                set textAlign(v) {}, get textAlign() { return ''; },
                set textBaseline(v) {}, get textBaseline() { return ''; },
                set direction(v) {}, get direction() { return ''; },
                set imageSmoothingEnabled(v) {}, get imageSmoothingEnabled() { return true; },
                set imageSmoothingQuality(v) {}, get imageSmoothingQuality() { return ''; },
            };
            let renderError = null;
            try {
                const fn = await loadRenderBattleFrame();
                fn(ctx, getGameState());
            } catch (e) { renderError = String(e && e.message || e); }
            return { ...counts, renderError };
        },
        // Run one AI update tick for a specific enemy outside the
        // normal GAME_TICK gate. Used by e2e to deterministically
        // observe the AI's attack-emission behavior (aimAngle pipeline,
        // shield-direction setting) without waiting on the random
        // attack cooldowns the controller schedules.
        tickAI: (enemyId) => {
            const gs = getGameState();
            const c = gs && gs.characters && gs.characters[enemyId];
            if (!c || !c.aiControllerInstance) return false;
            try {
                c.aiControllerInstance.update(16, gs);
                return true;
            } catch (e) {
                console.error('[testHooks] AI tick failed:', e);
                return false;
            }
        },
        // Force-fire one attack from `enemyId` toward the first
        // isPlayer character, going through the AI's exact aim path.
        // Bypasses cooldown gates so the attack lands on the very next
        // processQueue. Used by e2e to verify AI aiming end-to-end
        // without depending on RAF-driven sim timestamps.
        forceAIAttack: (enemyId) => {
            const gs = getGameState();
            const c = gs && gs.characters && gs.characters[enemyId];
            const player = c && Object.values(gs.characters).find(p => p && p.isPlayer);
            if (!c || !player || !c.attacks) return null;
            // Pick whichever attack key has a defined label.
            const key = Object.keys(c.attacks).find(k => c.attacks[k] && c.attacks[k].label);
            if (!key) return null;
            const attackData = c.attacks[key];
            // Reset cooldown so handleCharacterAttackIntent admits this.
            const label = attackData.label;
            if (c.attackCooldowns && c.attackCooldowns[label]) {
                c.attackCooldowns[label].lastUsed = 0;
            }
            const ax = c.x + c.width / 2;
            const ay = c.y + c.height / 2;
            const tx = player.x + player.width / 2;
            const ty = player.y + player.height / 2;
            const aimAngle = Math.atan2(ty - ay, tx - ax);
            queueEvent({
                type: EVENT_TYPES.CHARACTER_ATTACK_INTENT,
                payload: { id: enemyId, attackName: label, attackData, aimAngle },
            });
            return aimAngle;
        },
        injectAttack: (ownerId, opts = {}) => {
            const gs = getGameState();
            if (!gs) return null;
            const id = `dbg_atk_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
            const owner = gs.characters[ownerId];
            const angle = (typeof opts.aimAngle === 'number') ? opts.aimAngle : 0;
            const speed = (typeof opts.speed === 'number') ? opts.speed : 6;
            const sx = (owner ? owner.x + owner.width / 2 : 0);
            const sy = (owner ? owner.y + owner.height / 2 : 0);
            gs.activeAttacks[id] = {
                id,
                ownerId,
                name: 'debug',
                type: 'debug',
                x: sx,
                y: sy,
                width: opts.width || 40,
                height: opts.height || 30,
                velocityX: Math.cos(angle) * speed,
                velocityY: Math.sin(angle) * speed,
                damage: opts.damage || 1,
                lifetime: opts.lifetime || 4000,
                createdAt: gs.battleStatus.timestamp,
                fromPlayer: !!(owner && owner.isPlayer),
                color: 'rgba(0,0,0,0.5)',
            };
            return id;
        },
        // Headless Chromium frequently rejects requestPointerLock, so a
        // test that drives the real wheel engagement is flaky. This
        // sets the published aim state directly so the renderer-side +
        // M-keypress release logic can still be covered.
        setLocalAim: async (locked, angle) => {
            const gs = getGameState();
            if (!gs || !gs.battleStatus) return;
            gs.battleStatus.localAim = {
                locked: !!locked,
                angle: Number.isFinite(angle) ? angle : 0,
                radius: 140,
            };
            // Flip the real module-level aimLocked too so mouse handlers
            // take the "fire" branch instead of "request pointer lock".
            try {
                const fn = await loadTestForceAimLocked();
                fn(!!locked);
            } catch (_) {}
        },
        // Glass-shield e2e support: pin a character's block state without
        // depending on F-key + keyup races, and inflict a synthetic hit
        // directly through ATTACK_HIT_CHARACTER to exercise the shield
        // accounting (shieldHits++ / shieldShattered) deterministically.
        // Optionally pass a shieldAngle to set the directional facing —
        // omitted = use the character's existing angle.
        setBlocking: (characterId, blocking, shieldAngle) => {
            const gs = getGameState();
            const c = gs && gs.characters && gs.characters[characterId];
            if (!c) return;
            c.isBlocking = !!blocking;
            if (blocking) {
                c.shieldRaisedAt = gs.battleStatus.timestamp;
                if (typeof shieldAngle === 'number') c.shieldAngle = shieldAngle;
            } else {
                // Manual drop = clear cooldown too so tests can re-raise
                // immediately. Real F-presses install a cooldown via
                // PlayerSystem.BLOCK_INTENT.
                c.shieldCooldownUntil = 0;
            }
        },
        // Press-F simulation: dispatch a BLOCK_INTENT event the same way
        // battle-manager does on a real keydown. Honors cooldown +
        // shattered guards, so this is the test path for "did the
        // cooldown actually block re-raise".
        pressShield: (characterId, shieldAngle) => {
            queueEvent({
                type: EVENT_TYPES.PLAYER_INPUT_RECEIVED,
                payload: {
                    playerId: characterId,
                    inputType: 'BLOCK_INTENT',
                    shieldAngle: (typeof shieldAngle === 'number') ? shieldAngle : null
                }
            });
        },
        // Advance the sim's authoritative timestamp by `ms`. Test hook
        // for cooldown / auto-drop without sleeping in real time.
        advanceTime: (ms) => {
            const gs = getGameState();
            if (!gs || !gs.battleStatus) return;
            gs.battleStatus.timestamp = (gs.battleStatus.timestamp || 0) + (Number(ms) || 0);
        },
        // Queue an ABILITY_USE_REQUESTED for the per-tier Q ability. Used by
        // e2e specs to fire abilities without simulating real Q keypresses.
        useAbility: (casterId) => {
            queueEvent({ type: EVENT_TYPES.ABILITY_USE_REQUESTED, payload: { casterId } });
        },
        dealDamage: (attackerId, victimId, amount) => {
            queueEvent({
                type: EVENT_TYPES.ATTACK_HIT_CHARACTER,
                payload: {
                    attackId: `debug_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                    attackerId,
                    victimId,
                    damageAmount: Number(amount) || 10,
                },
            });
        },
        // Round / wave reset: fire WAVE_SPAWN_NEXT to exercise the
        // glass-shield reset hook in AttackSystem. Payload is a no-op
        // for the reset handler.
        advanceWave: () => {
            queueEvent({
                type: EVENT_TYPES.WAVE_SPAWN_NEXT,
                payload: { waveNumber: 99, enemyCount: 0 },
            });
        },
        getCharacter: (id) => {
            const gs = getGameState();
            return (gs && gs.characters && gs.characters[id]) || null;
        },
        // Multiplayer lives test hooks. Stage a synthetic multiplayer
        // session for e2e (we don't actually wire PeerJS in tests) so
        // we can exercise handleCharacterDied_Manager's respawn branch
        // and the live counter UI.
        configureMultiplayerLives: (livesPerPlayer) => {
            const gs = getGameState();
            if (!gs) return;
            gs.multiplayer.isMultiplayer = true;
            gs.multiplayer.isHost = true;
            gs.battleStatus.livesPerPlayer = Math.max(1, Math.floor(livesPerPlayer));
            for (const id in gs.characters) {
                const c = gs.characters[id];
                if (c && c.isPlayer) c.livesRemaining = gs.battleStatus.livesPerPlayer;
            }
        },
        getMultiplayerRoster: async () => {
            const m = await loadBattleManagerModule();
            return m._testGetMultiplayerRoster();
        },
        registerConnectedClient: async (client) => {
            const m = await loadBattleManagerModule();
            return m._testRegisterConnectedClient(client);
        },
        removePeerFromHostSession: async (peerId, opts) => {
            const m = await loadBattleManagerModule();
            return m.removePeerFromHostSession(peerId, opts);
        },
        configureMultiplayerSession: async (config) => {
            const m = await loadBattleManagerModule();
            return m._testConfigureMultiplayerSession(config);
        },
        simulateP2PMessage: async (senderPeerId, message) => {
            const m = await loadBattleManagerModule();
            return m.handleP2PMessage(senderPeerId, message);
        },
        setHostMessageAge: async (ageMs) => {
            const m = await loadBattleManagerModule();
            return m._testSetHostMessageAge(ageMs);
        },
        runHostHealthCheck: async () => {
            const m = await loadBattleManagerModule();
            return m._testRunHostHealthCheck();
        },
        applyHostMigration: async (payload) => {
            const m = await loadBattleManagerModule();
            return m.applyHostMigration(payload);
        },
        refreshHostMultiplayerHud: async () => {
            const m = await loadBattleManagerModule();
            return m.refreshHostMultiplayerHud();
        },
        // Set per-battle game modes from a test. Mirrors what the lobby
        // browser writes to battleStatus.gameModes when the host checks
        // the toggles. Pass any subset of {lifesteal, fatMode, glassCannon, lowGravity}.
        setGameModes: (modes) => {
            const gs = getGameState();
            if (!gs || !modes) return;
            gs.battleStatus.gameModes = {
                lifesteal: !!modes.lifesteal,
                fatMode: !!modes.fatMode,
                glassCannon: !!modes.glassCannon,
                lowGravity: !!modes.lowGravity,
            };
        },
        // Inflict a kill (synthetic) and run through the death pipeline
        // synchronously enough for tests to observe respawn behavior.
        killCharacter: (characterId, killerId) => {
            const gs = getGameState();
            const c = gs && gs.characters && gs.characters[characterId];
            if (!c) return;
            c.health = 0;
            queueEvent({
                type: EVENT_TYPES.CHARACTER_DIED,
                payload: { id: characterId, killerId: killerId || null }
            });
        },
        // Inject a synthetic ATTACK_HIT_CHARACTER event into the queue.
        // Used by tests to observe the damage pipeline (shield mitigation,
        // crit, Glass Cannon multiplier) without flying a projectile.
        queueAttackHit: ({ attackerId, victimId, damageAmount }) => {
            queueEvent({
                type: EVENT_TYPES.ATTACK_HIT_CHARACTER,
                payload: { attackId: null, attackerId, victimId, damageAmount }
            });
        },
        // Re-apply gravity-related game modes. Low Gravity is set once at
        // startBattleManager; this lets tests toggle it after battle has
        // already begun. Multiplies the theme default by 0.5 if the mode
        // is on; restores the default otherwise.
        recomputeGravity: () => {
            const gs = getGameState();
            if (!gs || !gs.battleStatus) return;
            const DEFAULT = 0.5; // BattlePhysics.DEFAULT_GRAVITY
            const modes = gs.battleStatus.gameModes || {};
            gs.battleStatus.currentGravity = modes.lowGravity ? DEFAULT * 0.5 : DEFAULT;
        },
    };
    console.log('[testHooks] window.__SNORDELUXE_DEBUG__ installed (localhost only).');
}
